import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { TeamHeader } from '../header/TeamHeader';
import { Loading } from '../../../ui/loading/Loading';
import StratIcon from '../../../../assets/components_icons/StratIcon';
import { OrgStatementTinyMceField } from '../../../admin/organizations/details/OrgStatementTinyMceField';
import { zynity_api } from '../../../../config/external-routes';
import { getOrganizationTeam } from '../../../../store/user_organizations/teams/team';
import { ContentEditorModal, ContentEditorValues } from './components/ContentEditorModal';
import { DetailsAccordion } from './components/DetailsAccordion';
import { MilestoneStyleAccordion } from './components/MilestoneStyleAccordion';
import './StrategiesPage.css';

type StrategyInitiative = {
    id: number;
    title: string;
    status: string | null;
    idCategory: number | null;
    idAuthor: number | null;
    backStory: string | null;
    description: string | null;
    progress: string | null;
    percentComplete: number | null;
    startDate: string | null;
    endDate: string | null;
};

type Category = { id: number; title: string };

type DiscussionMessage = { id: number; title: string; description: string | null };

const PERCENT_OPTIONS = Array.from({ length: 21 }, (_, i) => i * 5);

export const StrategiesWorksheetPage = () => {
    const { organization_id, team_id, initiative_id } = useParams();
    const isNew = !initiative_id || initiative_id === 'new';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const authenticatedUser = useSelector((state: any) => state.auth);
    const organizationTeam = useSelector((state: any) => state.organization_team);
    const enabled = !!authenticatedUser?.strategies_enabled;

    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'summary' | 'discussion'>('summary');
    const [categories, setCategories] = useState<Category[]>([]);
    // Confirmed live 2026-08-19: a brand-new worksheet's Status form control shows
    // "Preparing" pre-selected, but the actual created row ends up Active (reproduced
    // twice against the real API) — the backend's own default (StrategiesService::
    // createInitiative()) already agrees with this. Matching that here rather than
    // the form's own visually-misleading default.
    const [initiative, setInitiative] = useState<Partial<StrategyInitiative>>({ status: 'Active', percentComplete: 0 });
    const [authorIds, setAuthorIds] = useState<number[]>([]);
    const [messages, setMessages] = useState<DiscussionMessage[]>([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [savingMessage, setSavingMessage] = useState(false);

    useEffect(() => {
        if (organization_id && team_id) {
            dispatch(getOrganizationTeam({ organization_id, team_id }) as any);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization_id, team_id]);

    useEffect(() => {
        if (!organization_id || !team_id || !enabled) {
            return;
        }
        axios
            .get(
                zynity_api.organization_team_strategies_categories
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
            )
            .then((response) => setCategories(response.data.categories ?? []))
            .catch(() => setCategories([]));
    }, [organization_id, team_id, enabled]);

    useEffect(() => {
        if (isNew || !organization_id || !team_id || !initiative_id || !enabled) {
            return;
        }
        setLoading(true);
        axios
            .get(
                zynity_api.organization_team_strategies_item
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{initiative_id}', initiative_id)
            )
            .then((response) => {
                setInitiative(response.data.initiative ?? {});
                setAuthorIds(response.data.authors ?? []);
            })
            .catch(() => {
                Swal.fire({ icon: 'error', title: 'Load failed', text: 'This worksheet could not be loaded.' });
            })
            .finally(() => setLoading(false));
    }, [isNew, organization_id, team_id, initiative_id, enabled]);

    const fetchMessages = () => {
        if (isNew || !organization_id || !team_id || !initiative_id) {
            return;
        }
        setMessagesLoading(true);
        axios
            .get(
                zynity_api.organization_team_strategies_discussion
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{initiative_id}', initiative_id)
            )
            .then((response) => setMessages(response.data.messages ?? []))
            .catch(() => setMessages([]))
            .finally(() => setMessagesLoading(false));
    };

    useEffect(() => {
        if (activeTab === 'discussion') {
            fetchMessages();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, initiative_id]);

    // Matches Yii's TeamPeople-based isFacilitator check, which doesn't care about
    // isVisitor — the `members` relation alone excludes isVisitor='Yes' rows (that's
    // what `viewers` is for), which would otherwise exclude a visitor-facilitator
    // from both the allowEdit check and the Facilitators picker below. Both lists
    // share the same shape, safe to concatenate.
    const teamMembers: any[] = useMemo(
        () => [...(organizationTeam?.data?.members ?? []), ...(organizationTeam?.data?.viewers ?? [])],
        [organizationTeam?.data?.members, organizationTeam?.data?.viewers]
    );
    const facilitatorMembers = useMemo(
        () => teamMembers.filter((m: any) => m.isFacilitator === 'Yes'),
        [teamMembers]
    );

    // The team endpoint's `members` rows key on `id` (People.id), not `People_id`
    // (that alias only shows up on some other endpoints, e.g. MemberAccordion's) —
    // fall back the same way to stay correct against either shape.
    const memberId = (member: any) => (member?.People_id !== undefined && member?.People_id !== null ? member.People_id : member?.id);
    const memberLabel = (member: any) => member.KnownAs || `${member.FirstName ?? ''} ${member.LastName ?? ''}`.trim();

    const allowEdit = isNew
        || Number(initiative.idAuthor) === Number(authenticatedUser?.id)
        || authorIds.includes(Number(authenticatedUser?.id))
        || teamMembers.some((m: any) => Number(memberId(m)) === Number(authenticatedUser?.id) && m.isFacilitator === 'Yes');

    const setField = (field: keyof StrategyInitiative, value: any) => {
        setInitiative((current) => ({ ...current, [field]: value }));
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!organization_id || !team_id) {
            return;
        }
        if (!initiative.title || initiative.title.trim() === '') {
            return;
        }

        const payload = {
            title: initiative.title,
            status: initiative.status,
            idCategory: initiative.idCategory || null,
            startDate: initiative.startDate || null,
            endDate: initiative.endDate || null,
            percentComplete: initiative.percentComplete ?? 0,
            backStory: initiative.backStory || null,
            description: initiative.description || null,
            progress: initiative.progress || null,
            authors: authorIds,
        };

        setSaving(true);
        try {
            if (isNew) {
                const response = await axios.post(
                    zynity_api.organization_team_strategies
                        .replace('{organization_id}', organization_id)
                        .replace('{team_id}', team_id),
                    payload
                );
                const newId = response.data.initiative.id;
                navigate(`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/${newId}`, { replace: true });
            } else {
                const response = await axios.put(
                    zynity_api.organization_team_strategies_item
                        .replace('{organization_id}', organization_id)
                        .replace('{team_id}', team_id)
                        .replace('{initiative_id}', initiative_id as string),
                    payload
                );
                setInitiative(response.data.initiative);
                setAuthorIds(response.data.authors ?? []);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This worksheet could not be saved.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateMessage = async (values: ContentEditorValues) => {
        if (!organization_id || !team_id || !initiative_id) {
            return;
        }
        setSavingMessage(true);
        try {
            await axios.post(
                zynity_api.organization_team_strategies_discussion
                    .replace('{organization_id}', organization_id)
                    .replace('{team_id}', team_id)
                    .replace('{initiative_id}', initiative_id),
                { title: values.title, description: values.description || null }
            );
            setShowMessageModal(false);
            fetchMessages();
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Save failed', text: 'This topic could not be posted.' });
        } finally {
            setSavingMessage(false);
        }
    };

    if (!enabled) {
        return (
            <>
                <TeamHeader suffixRoute="/strategies" />
                <div className="container mt-5">
                    <p className="text-muted">This feature is not enabled for your account.</p>
                </div>
            </>
        );
    }

    if (loading) {
        return (
            <>
                <TeamHeader suffixRoute="/strategies" />
                <div className="container mt-5">
                    <Loading />
                </div>
            </>
        );
    }

    return (
        <>
            <TeamHeader suffixRoute="/strategies" />
            <div className="container mb-5 strategies-app strategies-worksheet">
                {/* Confirmed live 2026-08-27: the persistent "Strategies" app header
                (Categories/Initiatives nav + New Worksheet+) stays visible on the
                Worksheet page too, as its own row above the worksheet's own title/tabs
                row — previously missing here entirely, which meant leaving a worksheet
                had no way back except the browser's own back button. Both nav links
                stay real, clickable links here (matching `_strategies_app_header.php`'s
                `activeNav = 'worksheet'` — no pill active, but neither link disabled). */}
                <div className="strategies-app-header d-flex flex-wrap justify-content-between align-items-center mt-4">
                    <div className="d-flex align-items-center gap-2">
                        <StratIcon style={{ width: '30px', height: '30px' }} />
                        <div>
                            <h2 className="mb-0">Strategies</h2>
                            <div className="text-muted small">(Our Future)</div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Link
                            className="btn btn-teal"
                            to={`/organizations/${organization_id}/teams/${team_id}/strategies/worksheet/new`}
                        >
                            New Worksheet +
                        </Link>
                        <Dropdown>
                            <Dropdown.Toggle className="strategies-ellipsis-btn" aria-label="More actions">
                                •••
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end">
                                <Dropdown.Item
                                    as={Link}
                                    to={`/organizations/${organization_id}/teams/${team_id}/document-library`}
                                >
                                    Docs
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <Link
                        className="strategies-subnav-link"
                        to={`/organizations/${organization_id}/teams/${team_id}/strategies`}
                    >
                        Categories
                    </Link>
                    <Link
                        className="strategies-subnav-link"
                        to={`/organizations/${organization_id}/teams/${team_id}/strategies/initiatives`}
                    >
                        Initiatives
                    </Link>
                </div>

                <div className="strategies-app-header d-flex flex-wrap justify-content-between align-items-center mt-3">
                    <h2 className="mb-0">
                        Strategic Initiative Worksheet<sup aria-label="trademark">TM</sup>
                    </h2>
                    {!isNew && (
                        <div className="btn-group" role="tablist">
                            <Button
                                variant={activeTab === 'summary' ? 'warning' : 'outline-secondary'}
                                onClick={() => setActiveTab('summary')}
                            >
                                Summary
                            </Button>
                            <Button
                                variant={activeTab === 'discussion' ? 'warning' : 'outline-secondary'}
                                onClick={() => setActiveTab('discussion')}
                            >
                                Discussion
                            </Button>
                        </div>
                    )}
                </div>

                {activeTab === 'summary' && (
                    <Form className="mt-4 ws-v1-summary-panel" onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-sm-9">
                                <Form.Label>
                                    <b>Initiative Name</b>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    required
                                    disabled={!allowEdit}
                                    value={initiative.title ?? ''}
                                    onChange={(e) => setField('title', e.target.value)}
                                />
                            </div>
                            <div className="col-sm-3">
                                <Form.Label>
                                    <b>Status</b>
                                </Form.Label>
                                <Form.Select
                                    disabled={!allowEdit}
                                    value={initiative.status ?? 'Review'}
                                    onChange={(e) => setField('status', e.target.value)}
                                >
                                    <option value="Review">Preparing</option>
                                    <option value="Active">Active</option>
                                    <option value="Archived">Archived</option>
                                    <option value="Delete">Delete</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-6">
                                <Form.Label>
                                    <b>Category</b>
                                </Form.Label>
                                <Form.Select
                                    disabled={!allowEdit}
                                    value={initiative.idCategory ?? 0}
                                    onChange={(e) => setField('idCategory', Number(e.target.value) || null)}
                                >
                                    <option value={0}>-- Select Category --</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="col-md-6">
                                <Form.Label>
                                    <b>Facilitators</b>
                                </Form.Label>
                                {allowEdit ? (
                                    // Matches Yii's own rendered result (worksheet_v1/_header.php's
                                    // `.ws-v1-authors-select` is a native multi-select too, but it's
                                    // always enhanced client-side into a badge/pill picker by a jQuery
                                    // plugin — a raw scrollable listbox is never what's actually shown
                                    // live). Same badge classes as the read-only view below, each with
                                    // its own remove control, plus a single-select "add" dropdown
                                    // rather than porting a whole multiselect-plugin dependency.
                                    <div>
                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                            {authorIds.length === 0 && <span className="text-muted small">No facilitators selected.</span>}
                                            {authorIds.map((id) => {
                                                const member = facilitatorMembers.find((m: any) => Number(memberId(m)) === Number(id));
                                                if (!member) {
                                                    return null;
                                                }
                                                return (
                                                    <span
                                                        key={id}
                                                        className="badge bg-secondary d-inline-flex align-items-center gap-1"
                                                    >
                                                        {memberLabel(member)}
                                                        <button
                                                            type="button"
                                                            aria-label={`Remove ${memberLabel(member)}`}
                                                            onClick={() => setAuthorIds(authorIds.filter((a) => a !== id))}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: 'inherit',
                                                                padding: 0,
                                                                lineHeight: 1,
                                                                fontSize: '0.9em',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        {facilitatorMembers.some((m: any) => !authorIds.includes(Number(memberId(m)))) && (
                                            <Form.Select
                                                size="sm"
                                                value=""
                                                onChange={(e) => {
                                                    const id = Number(e.target.value);
                                                    if (id) {
                                                        setAuthorIds([...authorIds, id]);
                                                    }
                                                }}
                                            >
                                                <option value="">+ Add facilitator…</option>
                                                {facilitatorMembers
                                                    .filter((m: any) => !authorIds.includes(Number(memberId(m))))
                                                    .map((member: any) => (
                                                        <option key={memberId(member)} value={memberId(member)}>
                                                            {memberLabel(member)}
                                                        </option>
                                                    ))}
                                            </Form.Select>
                                        )}
                                    </div>
                                ) : (
                                    // Matches Yii's read-only Facilitators presentation
                                    // (worksheet_v1/_header.php) for non-authors: styled badges,
                                    // not a disabled multi-select — the original author gets a
                                    // distinct tan badge, every other facilitator a plain one.
                                    <div>
                                        {(() => {
                                            const originalAuthor = teamMembers.find(
                                                (m: any) => Number(memberId(m)) === Number(initiative.idAuthor)
                                            );
                                            return originalAuthor ? (
                                                <span className="badge ws-v1-badge-original me-1" title="Original Author">
                                                    {memberLabel(originalAuthor)}
                                                </span>
                                            ) : null;
                                        })()}
                                        {facilitatorMembers
                                            .filter((member: any) => Number(memberId(member)) !== Number(initiative.idAuthor))
                                            .filter((member: any) => authorIds.includes(Number(memberId(member))))
                                            .map((member: any) => (
                                                <span key={memberId(member)} className="badge bg-secondary me-1">
                                                    {memberLabel(member)}
                                                </span>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-md-4">
                                <Form.Label>
                                    <b>Start Date</b>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    disabled={!allowEdit}
                                    value={initiative.startDate ?? ''}
                                    onChange={(e) => setField('startDate', e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <Form.Label>
                                    <b>Completion Date</b>
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    disabled={!allowEdit}
                                    value={initiative.endDate ?? ''}
                                    onChange={(e) => setField('endDate', e.target.value)}
                                />
                            </div>
                            <div className="col-md-4">
                                <Form.Label>
                                    <b>Percent Completion</b>
                                </Form.Label>
                                <Form.Select
                                    disabled={!allowEdit}
                                    value={initiative.percentComplete ?? 0}
                                    onChange={(e) => setField('percentComplete', Number(e.target.value))}
                                >
                                    {PERCENT_OPTIONS.map((pct) => (
                                        <option key={pct} value={pct}>
                                            {pct}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                        </div>

                        <div className="mt-3">
                            <Form.Label>
                                <b>Back Story</b>
                            </Form.Label>
                            <div className="text-muted small mb-1">What context or events led to the creation of this initiative?</div>
                            <OrgStatementTinyMceField
                                id="worksheet_back_story"
                                name="backStory"
                                height={160}
                                disabled={!allowEdit}
                                value={initiative.backStory ?? ''}
                                onChange={(name, value) => setField(name as keyof StrategyInitiative, value)}
                            />
                        </div>

                        <div className="mt-3">
                            <Form.Label>
                                <b>Intent</b>
                            </Form.Label>
                            <div className="text-muted small mb-1">What are we trying to accomplish or change?</div>
                            <OrgStatementTinyMceField
                                id="worksheet_description"
                                name="description"
                                height={160}
                                disabled={!allowEdit}
                                value={initiative.description ?? ''}
                                onChange={(name, value) => setField(name as keyof StrategyInitiative, value)}
                            />
                        </div>

                        <div className="mt-3">
                            <Form.Label>
                                <b>Progress</b>
                            </Form.Label>
                            <div className="text-muted small mb-1">What movement or learning has occurred so far?</div>
                            <OrgStatementTinyMceField
                                id="worksheet_progress"
                                name="progress"
                                height={160}
                                disabled={!allowEdit}
                                value={initiative.progress ?? ''}
                                onChange={(name, value) => setField(name as keyof StrategyInitiative, value)}
                            />
                        </div>

                        {allowEdit && (
                            <div className="mt-4 d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    className="btn-strategies-cancel"
                                    onClick={() => navigate(`/organizations/${organization_id}/teams/${team_id}/strategies/initiatives`)}
                                >
                                    Cancel
                                </Button>
                                <Button type="submit" className="btn-strategies-cta" disabled={saving}>
                                    {saving ? 'Saving...' : 'Post'}
                                </Button>
                            </div>
                        )}
                    </Form>
                )}

                {activeTab === 'discussion' && !isNew && (
                    <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted small">Team-level discussion for this worksheet.</span>
                            {allowEdit && (
                                <Button className="btn-teal" size="sm" onClick={() => setShowMessageModal(true)}>
                                    + Add Topic
                                </Button>
                            )}
                        </div>
                        {messagesLoading ? (
                            <Loading />
                        ) : messages.length === 0 ? (
                            <p className="text-muted">No topics yet.</p>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {messages.map((message) => (
                                    <div key={message.id} className="strategies-category-card border rounded p-3">
                                        <div className="fw-bold">{message.title}</div>
                                        {message.description && <div className="text-muted small">{message.description}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {!isNew && organization_id && team_id && initiative_id && (
                    <DetailsAccordion
                        organizationId={organization_id}
                        teamId={team_id}
                        initiativeId={initiative_id}
                        allowEdit={allowEdit}
                        currentUserId={Number(authenticatedUser?.id)}
                    />
                )}

                {!isNew && organization_id && team_id && initiative_id && (
                    <MilestoneStyleAccordion
                        organizationId={organization_id}
                        teamId={team_id}
                        initiativeId={initiative_id}
                        allowEdit={allowEdit}
                        currentUserId={Number(authenticatedUser?.id)}
                        entityParam="next_step_id"
                        sectionLabel="Next Steps"
                        entityLabel="Next Step"
                        entityType="NextStep"
                        messageType="Message"
                        dateField="next_step_date"
                        dateLabel="Target date"
                        listUrl={zynity_api.organization_team_strategies_next_steps
                            .replace('{organization_id}', organization_id)
                            .replace('{team_id}', team_id)
                            .replace('{initiative_id}', initiative_id)}
                        reorderUrl={zynity_api.organization_team_strategies_next_steps_reorder
                            .replace('{organization_id}', organization_id)
                            .replace('{team_id}', team_id)
                            .replace('{initiative_id}', initiative_id)}
                        itemUrl={(id) =>
                            zynity_api.organization_team_strategies_next_step_item
                                .replace('{organization_id}', organization_id)
                                .replace('{team_id}', team_id)
                                .replace('{initiative_id}', initiative_id)
                                .replace('{next_step_id}', String(id))
                        }
                        listResponseKey="next_steps"
                    />
                )}

                {!isNew && organization_id && team_id && initiative_id && (
                    <MilestoneStyleAccordion
                        organizationId={organization_id}
                        teamId={team_id}
                        initiativeId={initiative_id}
                        allowEdit={allowEdit}
                        currentUserId={Number(authenticatedUser?.id)}
                        entityParam="milestone_id"
                        sectionLabel="Milestones"
                        entityLabel="Milestone"
                        entityType="Milestone"
                        messageType="Message"
                        dateField="milestone_date"
                        dateLabel="Target date"
                        listUrl={zynity_api.organization_team_strategies_milestones
                            .replace('{organization_id}', organization_id)
                            .replace('{team_id}', team_id)
                            .replace('{initiative_id}', initiative_id)}
                        reorderUrl={zynity_api.organization_team_strategies_milestones_reorder
                            .replace('{organization_id}', organization_id)
                            .replace('{team_id}', team_id)
                            .replace('{initiative_id}', initiative_id)}
                        itemUrl={(id) =>
                            zynity_api.organization_team_strategies_milestone_item
                                .replace('{organization_id}', organization_id)
                                .replace('{team_id}', team_id)
                                .replace('{initiative_id}', initiative_id)
                                .replace('{milestone_id}', String(id))
                        }
                        listResponseKey="milestones"
                    />
                )}

                <ContentEditorModal
                    show={showMessageModal}
                    title="New Topic"
                    titleLabel="Title"
                    saving={savingMessage}
                    onHide={() => setShowMessageModal(false)}
                    onSubmit={handleCreateMessage}
                />
            </div>
        </>
    );
};
